import { useState } from "react";
import { toast } from "sonner";
import { MapPinned, Package, ToggleLeft, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const FEATURES = [
  {
    key: "map_enabled",
    label: "Kart og turplanlegging",
    desc: "Eiga side «Kart» med maskiner og fristar, turplanlegging, GPS-plassering i rapportar og overstyrt maskinplassering.",
    icon: MapPinned,
  },
  {
    key: "parts_page_enabled",
    label: "Global side «Oljer & filter»",
    desc: "Samla bestillingsliste på tvers av maskiner. Servicekortet på kvar maskin er alltid på uansett.",
    icon: Package,
  },
];

/** Admin: switch optional modules on/off (they are built, but hidden in pilot). */
export const FeatureToggles = () => {
  const { features, setFeatures, isAdmin } = useAuth();
  const [busy, setBusy] = useState(null);

  const toggle = async (key, on) => {
    setBusy(key);
    try {
      const r = await api.put("/settings/features", { [key]: on });
      setFeatures({ ...features, ...r.data });
      localStorage.setItem("vms_features", JSON.stringify({ ...features, ...r.data }));
      toast.success(on ? "Funksjonen er slått på og synleg i menyen." : "Funksjonen er skjult. Ingen data er sletta.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje endre funksjon.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="shadow-sm" data-testid="feature-toggles-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><ToggleLeft className="h-4 w-4 text-primary" /> Funksjonar</CardTitle>
        <CardDescription>Modular som er bygde, men kan haldast skjulte til de treng dei. Data blir aldri sletta når noko blir skjult.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {FEATURES.map((f) => {
          const Icon = f.icon;
          const on = !!features?.[f.key];
          return (
            <label key={f.key} className={cn("flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors duration-150", on ? "border-primary/40 bg-primary/5" : "hover:bg-accent/60")} data-testid={`feature-${f.key}`}>
              <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-lg", on ? "bg-primary text-[#0B0D10]" : "bg-muted text-muted-foreground")}>
                {busy === f.key ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  {f.label}
                  <Badge variant={on ? "default" : "secondary"} className="text-[10px]" data-testid={`feature-${f.key}-state`}>{on ? "På" : "Skjult"}</Badge>
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{f.desc}</span>
              </span>
              <Switch checked={on} disabled={!isAdmin || busy === f.key} onCheckedChange={(v) => toggle(f.key, v)} aria-label={f.label} data-testid={`feature-${f.key}-switch`} />
            </label>
          );
        })}
      </CardContent>
    </Card>
  );
};
